import { useState, useCallback, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import { useRef } from "react";
import PersonForm from "./components/PersonForm";
import MonthForm from "./components/MonthForm";
import WorkTable from "./components/WorkTable";
import "./App.css";
import CONSTS from "./shared/constants.json";

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
const getWeekday = (year, month, day) => {
  const d = new Date(year, month - 1, day);
  return ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
};

export default function App() {
  const [tab, setTab] = useState("month"); // "month" | "person" | "preview"
  const [status, setStatus] = useState(null);

  // ── 月次情報 ─────────────────────────────────────────────
  const [monthInfo, setMonthInfo] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    kenkyuShubetsu: "その他研究費",
    kenkyuShumoku: (CONSTS.KENKYU_Sonota && CONSTS.KENKYU_Sonota[0]) || "",
    daihyoushaKubun: "",
    daishaKbn: "",
    kanrishaShozoku: "",
    kanrishaName: "中村聡史",
    hourlyWage: 1250,
  });

  const initRows = useCallback((year, month) => {
    const n = getDaysInMonth(year, month);
    return Array.from({ length: n }, (_, i) => ({
      day: i + 1,
      weekday: getWeekday(year, month, i + 1),
      startTime: "",
      endTime: "",
      breakTime: "",
      workHours: "",
      lateHours: "",
      totalHours: "",
      content: "",
      active: false,
    }));
  }, []);

  // ── 従事者情報（profile.nkmr.io 連携） ──────────────────────
  const [person, setPerson] = useState({});
  const [profileStatus, setProfileStatus] = useState("loading");
  const [profileError, setProfileError] = useState("");
  const [rows, setRows] = useState(() => {
    try { return JSON.parse(localStorage.getItem('meiji_rows')) || initRows(monthInfo.year, monthInfo.month); } catch (e) { return initRows(monthInfo.year, monthInfo.month); }
  });
  // ── プレビュー用 state ─────────────────────────────────────
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const previewIFrameRef = useRef(null);
  const previewPdfIframeRef = useRef(null);

  // 勤務行だけを端末に一時保存する。住所・口座を含むプロフィールは保存しない。
  useEffect(() => { localStorage.setItem('meiji_rows', JSON.stringify(rows)); }, [rows]);

  const loadProfile = useCallback(async () => {
    setProfileStatus("loading");
    setProfileError("");
    try {
      const res = await fetch("/api/person/me", {
        credentials: "same-origin",
        headers: { "X-Return-To": window.location.href },
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 && data.loginUrl) {
        window.location.assign(data.loginUrl);
        return;
      }
      if (!res.ok) throw new Error(data.error || "プロフィールを取得できませんでした");
      const p = data.person || {};
      setPerson(p);
      setMonthInfo(prev => ({
        ...prev,
        kanrishaShozoku: p.faculty || prev.kanrishaShozoku,
      }));
      setProfileStatus("ready");
    } catch (error) {
      console.error(error);
      setProfileStatus("error");
      setProfileError(
        error.message === "profile service is not configured"
          ? "プロフィール連携のサービス鍵が未設定です。"
          : "プロフィールを取得できませんでした。時間をおいて再読込してください。"
      );
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const openProfileEditor = useCallback(() => {
    const profileWindow = window.open("https://profile.nkmr.io/", "_blank", "noopener,noreferrer");
    if (!profileWindow) {
      window.location.assign("https://profile.nkmr.io/");
      return;
    }

    // profile のタブからこの画面へ戻ったとき、登録内容を自動反映する。
    const refreshOnReturn = () => {
      window.removeEventListener("focus", refreshOnReturn);
      loadProfile();
    };
    window.setTimeout(() => window.addEventListener("focus", refreshOnReturn), 500);
  }, [loadProfile]);

  // ── 勤務行 ───────────────────────────────────────────────
  const numDays = useMemo(
    () => getDaysInMonth(monthInfo.year, monthInfo.month),
    [monthInfo.year, monthInfo.month]
  );

  // const [rows, setRows] = useState(() => initRows(monthInfo.year, monthInfo.month));

  const handleMonthChange = useCallback((field, value) => {
    setMonthInfo(prev => {
      const next = { ...prev, [field]: value };
      if (field === "year" || field === "month") {
        setRows(initRows(next.year, next.month));
      }
      return next;
    });
  }, [initRows]);

  const updateRow = useCallback((idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      // 従事時間数の自動計算
      if (field === "startTime" || field === "endTime" || field === "breakTime") {
        const r = next[idx];
        if (r.startTime && r.endTime) {
          const toMin = (t) => {
            const [h, m] = t.split(":").map(Number);
            return h * 60 + (m || 0);
          };
          const breakMin = r.breakTime ? toMin(r.breakTime) : 0;
          const totalMin = toMin(r.endTime) - toMin(r.startTime) - breakMin;
          if (totalMin > 0) {
            const lateStart = Math.max(toMin(r.startTime), 22 * 60);
            const lateMin = Math.max(0, toMin(r.endTime) - lateStart);
            const workMin = totalMin - lateMin;
            const fmt = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
            next[idx].workHours = fmt(workMin);
            next[idx].lateHours = fmt(lateMin);
            next[idx].totalHours = fmt(workMin + lateMin);
          }
        }
      }
      return next;
    });
  }, []);

  const toggleRow = useCallback((idx) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], active: !next[idx].active };
      if (!next[idx].active) {
        next[idx] = { ...next[idx], startTime: "", endTime: "", breakTime: "", workHours: "", lateHours: "", totalHours: "", content: "" };
      }
      return next;
    });
  }, []);

  // ── Excel出力 ────────────────────────────────────────────
  const handleExport = async () => {
    setStatus("loading");
    try {
      const activeRows = rows.filter(r => r.active);
      // monthInfo に管理者所属が無ければ person.faculty をフォールバック
      const monthToSend = { ...monthInfo, kanrishaShozoku: monthInfo?.kanrishaShozoku || person.faculty || "" };
      const res = await fetch("/api/excel/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          person,
          month: monthToSend,
          rows: activeRows,
          hourlyWage: monthInfo.hourlyWage,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `勤務実績報告書_${monthInfo.year}年${monthInfo.month}月_${person.name}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus("done");
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setTimeout(() => setStatus(null), 4000);
    }
  };

  // ── プレビュー ────────────────────────────────────────────
  const handlePreview = async () => {
    try {
      const activeRows = rows.filter(r => r.active);
      const monthToSend = { ...monthInfo, kanrishaShozoku: monthInfo?.kanrishaShozoku || person.faculty || "" };
      const res = await fetch("/api/excel/publish-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ person, month: monthToSend, rows: activeRows, hourlyWage: monthInfo.hourlyWage }),
      });
      if (!res.ok) throw new Error(await res.text());
      // const { url } = await res.json();

      // // 1) そのまま新タブで開く（ブラウザが処理できれば）
      // // window.open(url, "_blank");

      // const backendHost = window.location.hostname || "localhost";
      // const backendPort = 3001;
      // const url2 = `http://${backendHost}:${backendPort}/public/${encodeURIComponent(fname)}`;
      // console.log("preview url ->", url2);
      // window.open(url2, "_blank");
      const { fname } = await res.json();
      // Apache が /public をバックエンドへ中継するため、同一オリジンのURLを使う。
      // これにより本番HTTPSでも mixed content にならない。
      const previewUrl = `/public/${encodeURIComponent(fname)}`;
      console.log("preview url ->", previewUrl);
      window.open(previewUrl, "_blank");

      // 2) Office Online で開く（公開 https URL が必要。ローカルは ngrok 等で公開してから）
      // const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      // window.open(officeUrl, "_blank");
    } catch (e) {
      console.error(e);
      alert("プレビューに失敗しました: " + (e.message || e));
    }
  };

  // 印刷ボタン（簡易実装：現在のページをブラウザの印刷ダイアログで印刷）
  const handlePrint = () => {
    window.print();
  };

  const totalWorkMin = rows.reduce((sum, r) => {
    if (!r.workHours) return sum;
    const [h, m] = r.workHours.split(":").map(Number);
    return sum + h * 60 + (m || 0);
  }, 0);
  const totalH = Math.floor(totalWorkMin / 60);
  const totalM = totalWorkMin % 60;
  const estimatedPay = Math.ceil(totalWorkMin / 60 * monthInfo.hourlyWage);

  return (
    <div className="app-shell">
      {/* プレビュー・モーダル */}
      {previewHtml && (
        <div className="preview-modal" style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200
        }}>
          <div style={{ width: "92%", height: "88%", background: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.3)", borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: 8, display: "flex", gap: 8, justifyContent: "flex-end", borderBottom: "1px solid #eee" }}>
              <button className="btn" onClick={() => previewIFrameRef.current?.contentWindow?.print()}>印刷</button>
              <button className="btn" onClick={() => { setPreviewHtml(null); }}>閉じる</button>
            </div>
            <iframe
              ref={previewIFrameRef}
              title="勤務実績プレビュー"
              srcDoc={previewHtml}
              style={{ border: 0, width: "100%", height: "100%" }}
            />
          </div>
        </div>
      )}
      {/* ── Toolbar ── */}
      <header className="toolbar">
        <div className="toolbar-left">
          <span className="app-icon">📋</span>
          <div>
            <h1 className="app-title">勤務実績報告書 入力システム</h1>
            <p className="app-subtitle">明治大学 研究補助業務（アルバイト）申請書</p>
          </div>
        </div>
        <div className="toolbar-right">
          {status === "done" && <span className="badge success">✓ Excel出力しました</span>}
          {status === "error" && <span className="badge error">⚠ エラーが発生しました</span>}
          {status === "loading" && <span className="badge loading">生成中...</span>}
          <button className="btn btn-primary" onClick={handleExport} disabled={status === "loading"}>
            ↓ 勤務実績報告書を出力
          </button>
          {/* プレビュー機能はコードを残し、現在は画面に表示しない。 */}
          {/*
          <button className="btn" onClick={handlePreview} style={{ marginLeft: 8 }}>
            🔍 プレビュー
          </button>
          */}
          <a
            className="btn"
            href="https://fund.nkmr.io/#ledger/human"
            target="_blank"
            rel="noreferrer"
            style={{ marginLeft: 8 }}
          >
            研究費登録
          </a>
          {/* <button className="btn" onClick={handlePrint} style={{ marginLeft: 8 }}>
            🖨 印刷する
          </button> */}
        </div>
      </header>

      {/* ── タブ ── */}
      <div className="tab-bar">
        {[["month", "① 月次・研究情報"], ["person", "② 従事者情報"], ["work", "③ 勤務実績入力"]].map(([key, label]) => (
          <button key={key} className={`tab-btn${tab === key ? " active" : ""}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      <main className="main-content">
        {tab === "month" && <MonthForm info={monthInfo} onChange={handleMonthChange} />}
        {tab === "person" && (
          <PersonForm
            person={person}
            profileStatus={profileStatus}
            profileError={profileError}
            onReload={loadProfile}
            onEditProfile={openProfileEditor}
          />
        )}
        {tab === "work" && (
          <WorkTable
            rows={rows}
            year={monthInfo.year}
            month={monthInfo.month}
            onToggle={toggleRow}
            onUpdate={updateRow}
            totalH={totalH}
            totalM={totalM}
            estimatedPay={estimatedPay}
            hourlyWage={monthInfo.hourlyWage}
          />
        )}
      </main>
    </div>
  );
}
